/**
 * @file
 * Blade Slider component behavior.
 */
(function (Drupal, once, dataLayer) {
  'use strict';

  /**
   * Apply BEM state classes to image and content slides for a given index.
   * - Progressive sizing for images via CSS classes
   * - Sync content visibility with current index
   * - Manage pagination state
   *
   * @param {HTMLElement} root - Slider root element ('.blade-slider').
   * @param {number} currentIndex - Index of the active slide.
   * @param {number} lastIndex - Index of the previously active slide.
   * @param {number} total - Total number of slides (paired by index).
   * @param {number} direction - Navigation direction for sliding animations.
   * @param {boolean} updateDataLayer - Whether to enable CSS transitions on this update.
   */
  function updateSliderState(root, currentIndex, lastIndex, total, direction, updateDataLayer = true) {
    const imageSlides = Array.from(root.querySelectorAll('.blade-slider__image-slide'));
    const contentSlides = Array.from(root.querySelectorAll('.blade-slider__content-slide'));
    const dots = Array.from(root.querySelectorAll('.blade-slider__pagination .blade-slider__dot'));

    const assigned = new Set();

    // Clear all state classes and reset transitions
    for (let idx = 0; idx < imageSlides.length; idx += 1) {
      const slide = imageSlides[idx];
      slide.className = 'blade-slider__image-slide';
    }

    // Calculate indices with wrapping
    const prevIndex = (currentIndex - 1 + total) % total;
    const nextIndex = (currentIndex + 1) % total;
    const prevFarIndex = (currentIndex - 2 + total) % total;
    const nextFarIndex = (currentIndex + 2) % total;

    // Assign primary states
    if (imageSlides[currentIndex]) {
      imageSlides[currentIndex].classList.add('blade-slider__image-slide--active');
      assigned.add(currentIndex);
    }
    if (imageSlides[prevIndex]) {
      imageSlides[prevIndex].classList.add('blade-slider__image-slide--prev');
      assigned.add(prevIndex);
    }
    if (imageSlides[nextIndex]) {
      imageSlides[nextIndex].classList.add('blade-slider__image-slide--next');
      assigned.add(nextIndex);
    }

    // Far previews: only add if not already assigned to avoid conflicts
    if (!assigned.has(nextFarIndex) && imageSlides[nextFarIndex]) {
      imageSlides[nextFarIndex].classList.add('blade-slider__image-slide--next-far');
      assigned.add(nextFarIndex);
    }
    if (!assigned.has(prevFarIndex) && imageSlides[prevFarIndex]) {
      imageSlides[prevFarIndex].classList.add('blade-slider__image-slide--prev-far');
      assigned.add(prevFarIndex);
    }

    // Handle sliding out with symmetrical direction-based behavior
    if (lastIndex !== currentIndex && imageSlides[lastIndex] && !assigned.has(lastIndex)) {
      const slideOutClass = direction === 1 ? 'blade-slider__image-slide--sliding-out' : 'blade-slider__image-slide--sliding-out-right';
      imageSlides[lastIndex].classList.add(slideOutClass);
    }

    // Root modifiers for responsive layout behaviors
    root.classList.toggle('blade-slider--single', total === 1);
    root.classList.toggle('blade-slider--two', total === 2);

    // Sync content slides
    for (let idx = 0; idx < contentSlides.length; idx += 1) {
      const isActive = idx === currentIndex;
      contentSlides[idx].classList.toggle('blade-slider__content-slide--active', isActive);
      contentSlides[idx].setAttribute('aria-hidden', isActive ? 'false' : 'true');
    }

    // Update dots
    for (let idx = 0; idx < dots.length; idx += 1) {
      const isActive = idx === currentIndex;
      dots[idx].classList.toggle('blade-slider__dot--active', isActive);
      dots[idx].setAttribute('aria-current', isActive ? 'true' : 'false');
    }

    // Add to data layer
    const currentSlideTitle = contentSlides[currentIndex].querySelector('.blade-slide-content__title');

    if (!currentSlideTitle) {
      return;
    }

    const title = currentSlideTitle.innerText?.trim() || '';

    if (currentIndex !== lastIndex) {
      dataLayer.push({
        'event': 'carousel-slide',
        'data-analytics-carousel-name': title,
        'data-analytics-carousel-position': currentIndex + 1
      });
    }
  }

  /**
   * Initialize a Blade Slider root element.
   * @param {HTMLElement} root - Slider root element ('.blade-slider').
   */
  function initBladeSlider(root) {
    const imageSlides = root.querySelectorAll('.blade-slider__image-slide');
    const contentSlides = root.querySelectorAll('.blade-slider__content-slide');
    const total = Math.min(imageSlides.length, contentSlides.length);
    if (!total) {
      return; // nothing to do without paired slides
    }

    // Paginate to image's slide on click
    imageSlides.forEach((slide, idx) => {
      slide.addEventListener('click', () => {
        updateSliderState(root, idx, currentIndex, total, direction, true);
      });
    });

    let currentIndex = 0;
    let lastIndex = 0;
    let direction = 1; // Track navigation direction for sliding animations

    const prevBtn = root.querySelector('.blade-slider__navigation--prev');
    const nextBtn = root.querySelector('.blade-slider__navigation--next');
    const pagination = root.querySelector('.blade-slider__pagination');

    // Build dots
    if (pagination && pagination.children.length === 0) {
      const frag = document.createDocumentFragment();
      for (let idx = 0; idx < total; idx += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'blade-slider__dot';
        dot.setAttribute('data-index', String(idx));
        dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
        frag.appendChild(dot);
      }
      pagination.appendChild(frag);

      // Event delegation to avoid per-dot closures
      pagination.addEventListener('click', function onPaginationClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        const dot = target.closest('.blade-slider__dot');
        if (!dot) {
          return;
        }
        const nextIdx = Number(dot.getAttribute('data-index'));
        if (Number.isNaN(nextIdx)) {
          return;
        }
        lastIndex = currentIndex;
        direction = currentIndex < nextIdx ? 1 : -1; // Determine direction based on index jump
        currentIndex = nextIdx;
        updateSliderState(root, currentIndex, lastIndex, total, direction, true);
      });
    }

    /**
     * Navigate relative by direction.
     * @param {number} dir - -1 for previous, +1 for next.
     */
    function navigate(dir) {
      lastIndex = currentIndex;
      direction = dir; // Store direction for sliding out animation
      currentIndex = (currentIndex + dir + total) % total;
      updateSliderState(root, currentIndex, lastIndex, total, direction, true);
    }

    // Attach navigation listeners
    if (nextBtn) {
      nextBtn.addEventListener('click', function onNext() { navigate(1); });
      nextBtn.addEventListener('keydown', function onKey(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(1);
        }
      });
    }
    if (prevBtn) {
      prevBtn.addEventListener('click', function onPrev() { navigate(-1); });
      prevBtn.addEventListener('keydown', function onKey(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(-1);
        }
      });
    }

    // Initial paint without transitions
    updateSliderState(root, currentIndex, lastIndex, total, direction, false);
  }

  Drupal.behaviors.bladeSlider = {
    attach: function attach(context) {
      once('blade-slider', '.blade-slider', context).forEach(initBladeSlider);
    }
  };
})(Drupal, once, window.dataLayer);
